import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  Calendar, Clock, Building2, Mail, Phone, FileText, Loader2, Trash2,
  User, Search, Globe, Briefcase, ChevronRight, X, Send, Video,
  MessageSquare, Copy, ArrowUpDown, StickyNote, Plus, Edit, ExternalLink,
  AlertCircle, CheckCircle2, XCircle, Eye, RefreshCw, Flag, UserCheck,
  CalendarCheck, History
} from "lucide-react";
import { toast } from "sonner";

const STATUS_CONFIG = {
  pending:     { label: "Pending",     icon: Clock,         color: "bg-yellow-500/15 text-yellow-600 border-yellow-500/30" },
  contacted:   { label: "Contacted",   icon: Phone,         color: "bg-sky-500/15 text-sky-600 border-sky-500/30" },
  scheduled:   { label: "Scheduled",   icon: Calendar,      color: "bg-indigo-500/15 text-indigo-600 border-indigo-500/30" },
  confirmed:   { label: "Confirmed",   icon: CheckCircle2,  color: "bg-blue-500/15 text-blue-600 border-blue-500/30" },
  completed:   { label: "Completed",   icon: CheckCircle2,  color: "bg-green-500/15 text-green-600 border-green-500/30" },
  no_show:     { label: "No Show",     icon: XCircle,       color: "bg-orange-500/15 text-orange-600 border-orange-500/30" },
  closed_won:  { label: "Closed Won",  icon: CheckCircle2,  color: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" },
  closed_lost: { label: "Closed Lost", icon: XCircle,       color: "bg-red-500/15 text-red-600 border-red-500/30" },
  spam:        { label: "Spam",        icon: AlertCircle,   color: "bg-gray-500/15 text-gray-500 border-gray-500/30" },
  archived:    { label: "Archived",    icon: FileText,      color: "bg-gray-500/15 text-gray-400 border-gray-400/30" },
  cancelled:   { label: "Cancelled",   icon: XCircle,       color: "bg-red-500/15 text-red-500 border-red-500/30" },
};

const PRIORITY_CONFIG = {
  low:    { label: "Low",    color: "text-gray-500" },
  medium: { label: "Med",    color: "text-blue-500" },
  high:   { label: "High",   color: "text-orange-500" },
  urgent: { label: "Urgent", color: "text-red-500" },
};

const TIMELINE_ICONS = {
  request_submitted: Calendar,
  status_changed: RefreshCw,
  note_added: StickyNote,
  email_sent: Send,
  zoom_added: Video,
  zoom_updated: Video,
  assigned: UserCheck,
  priority_changed: Flag,
};

const EMAIL_TEMPLATES = [
  { value: "scheduled", label: "Demo Scheduled Confirmation" },
  { value: "zoom_details", label: "Zoom Meeting Details" },
  { value: "reminder", label: "Demo Reminder" },
  { value: "thank_you", label: "Thank You After Demo" },
  { value: "no_show_followup", label: "No-Show Follow-Up" },
  { value: "custom", label: "Custom Message" },
];

const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const AdminDemoRequests = () => {
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [selectedDemo, setSelectedDemo] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchDemos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/demo/requests`, getAuth());
      setDemos(res.data);
    } catch { toast.error("Failed to load demo requests"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDemos(); }, [fetchDemos]);

  const counts = useMemo(() => demos.reduce((acc, d) => { acc[d.status] = (acc[d.status] || 0) + 1; return acc; }, {}), [demos]);

  const filtered = useMemo(() => {
    let list = demos;
    if (filterStatus !== "all") list = list.filter(d => d.status === filterStatus);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(d =>
        d.name?.toLowerCase().includes(q) || d.company?.toLowerCase().includes(q) ||
        d.email?.toLowerCase().includes(q) || d.phone?.includes(q) || d.demo_id?.includes(q)
      );
    }
    list = [...list].sort((a, b) => {
      if (sortBy === "created_at") return (b.created_at || "").localeCompare(a.created_at || "");
      if (sortBy === "preferred_date") return (a.preferred_date || "").localeCompare(b.preferred_date || "");
      if (sortBy === "company") return (a.company || "").localeCompare(b.company || "");
      if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "status") return (a.status || "").localeCompare(b.status || "");
      if (sortBy === "priority") {
        const order = { urgent: 0, high: 1, medium: 2, low: 3 };
        return (order[a.priority] ?? 2) - (order[b.priority] ?? 2);
      }
      return 0;
    });
    return list;
  }, [demos, filterStatus, search, sortBy]);

  const openDetail = (demo) => { setSelectedDemo(demo); setDetailOpen(true); };
  const refreshDetail = async (demoId) => {
    try {
      const res = await axios.get(`${API}/demo/requests/${demoId}`, getAuth());
      setSelectedDemo(res.data);
      setDemos(prev => prev.map(d => d.demo_id === demoId ? res.data : d));
    } catch { /* silent */ }
  };

  const deleteDemo = async (demoId) => {
    if (!window.confirm("Delete this demo request permanently?")) return;
    try {
      await axios.delete(`${API}/demo/requests/${demoId}`, getAuth());
      setDemos(prev => prev.filter(d => d.demo_id !== demoId));
      if (selectedDemo?.demo_id === demoId) setDetailOpen(false);
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  };

  // Pipeline stats - show key statuses
  const pipelineStatuses = ["pending", "contacted", "scheduled", "confirmed", "completed", "closed_won", "no_show"];

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-demo-requests">
      {/* Pipeline Stats */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
        <button onClick={() => setFilterStatus("all")}
          className={`shrink-0 px-3 py-2 rounded-xl border text-center transition-all min-w-[70px] ${filterStatus === "all" ? "ring-2 ring-[#FFCC00] border-[#FFCC00]/50" : "border-border hover:border-[#FFCC00]/30"}`}>
          <div className="text-lg font-bold text-foreground">{demos.length}</div>
          <div className="text-[10px] text-muted-foreground">All</div>
        </button>
        {pipelineStatuses.map(key => {
          const cfg = STATUS_CONFIG[key];
          return (
            <button key={key} onClick={() => setFilterStatus(filterStatus === key ? "all" : key)}
              className={`shrink-0 px-3 py-2 rounded-xl border text-center transition-all min-w-[70px] ${filterStatus === key ? "ring-2 ring-[#FFCC00] border-[#FFCC00]/50" : "border-border hover:border-[#FFCC00]/30"}`}>
              <div className="text-lg font-bold text-foreground">{counts[key] || 0}</div>
              <div className="text-[10px] text-muted-foreground">{cfg.label}</div>
            </button>
          );
        })}
      </div>

      {/* Search + Sort */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, company, email..." className="pl-10 h-9" data-testid="demo-search" />
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[180px] h-9" data-testid="demo-sort">
            <ArrowUpDown className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="created_at">Newest First</SelectItem>
            <SelectItem value="preferred_date">Demo Date</SelectItem>
            <SelectItem value="company">Company</SelectItem>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="status">Status</SelectItem>
            <SelectItem value="priority">Priority</SelectItem>
          </SelectContent>
        </Select>
        {filterStatus !== "all" && (
          <Button variant="outline" size="sm" onClick={() => setFilterStatus("all")} className="h-9 text-xs shrink-0">
            <X className="w-3 h-3 mr-1" /> {STATUS_CONFIG[filterStatus]?.label}
          </Button>
        )}
      </div>

      {/* Results count */}
      <p className="text-xs text-muted-foreground">{filtered.length} request{filtered.length !== 1 ? "s" : ""}</p>

      {/* List */}
      {filtered.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-12 text-center">
          <Calendar className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground text-sm">No demo requests found</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(demo => {
            const cfg = STATUS_CONFIG[demo.status] || STATUS_CONFIG.pending;
            const pri = PRIORITY_CONFIG[demo.priority] || PRIORITY_CONFIG.medium;
            const StatusIcon = cfg.icon;
            return (
              <Card key={demo.demo_id} className="border-border hover:border-[#FFCC00]/30 transition-colors cursor-pointer group" data-testid={`demo-card-${demo.demo_id}`}
                onClick={() => openDetail(demo)}>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-center gap-3">
                    {/* Status icon */}
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${cfg.color.split(" ")[0]}`}>
                      <StatusIcon className={`w-4 h-4 ${cfg.color.split(" ")[1]}`} />
                    </div>
                    {/* Main info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-foreground text-sm truncate">{demo.name}</span>
                        <span className="text-muted-foreground text-xs hidden sm:inline">·</span>
                        <span className="text-muted-foreground text-xs truncate hidden sm:inline">{demo.company}</span>
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${cfg.color}`}>{cfg.label}</Badge>
                        {demo.interested_plan && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" data-testid={`demo-plan-badge-${demo.demo_id}`}>
                            {demo.interested_plan}
                          </Badge>
                        )}
                        {demo.priority && demo.priority !== "medium" && (
                          <span className={`text-[10px] font-bold ${pri.color}`}>{pri.label}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {demo.scheduled_date || demo.preferred_date}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {demo.scheduled_time || demo.preferred_time}</span>
                        <span className="hidden sm:flex items-center gap-1"><Mail className="w-3 h-3" /> {demo.email}</span>
                        {demo.zoom_meeting?.link && <span className="flex items-center gap-1 text-blue-500"><Video className="w-3 h-3" /> Zoom</span>}
                      </div>
                    </div>
                    {/* Arrow */}
                    <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-foreground transition-colors shrink-0" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail Drawer */}
      {selectedDemo && (
        <DemoDetailDialog
          demo={selectedDemo}
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          onRefresh={() => refreshDetail(selectedDemo.demo_id)}
          onDelete={() => deleteDemo(selectedDemo.demo_id)}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Detail Dialog
// ─────────────────────────────────────────────────────────────
const DemoDetailDialog = ({ demo, open, onClose, onRefresh, onDelete }) => {
  const [tab, setTab] = useState("details");
  const [saving, setSaving] = useState(false);

  // Inline edits
  const updateField = async (field, value) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/demo/requests/${demo.demo_id}`, { [field]: value }, getAuth());
      onRefresh();
      toast.success("Updated");
    } catch { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  const cfg = STATUS_CONFIG[demo.status] || STATUS_CONFIG.pending;
  const tabs = [
    { key: "details", label: "Details", icon: Eye },
    { key: "zoom", label: "Zoom", icon: Video },
    { key: "notes", label: "Notes", icon: StickyNote },
    { key: "email", label: "Email", icon: Send },
    { key: "timeline", label: "Timeline", icon: History },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0" data-testid="demo-detail-dialog">
        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-border shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-foreground font-['Manrope']">{demo.name}</h2>
              <p className="text-sm text-muted-foreground">{demo.company} · {demo.email}</p>
            </div>
            <Badge variant="outline" className={`text-xs px-2 py-0.5 ${cfg.color}`}>{cfg.label}</Badge>
          </div>
          {/* Tabs */}
          <div className="flex gap-1 mt-3 overflow-x-auto">
            {tabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                  tab === t.key ? "bg-[#FFCC00]/15 text-[#FFCC00]" : "text-muted-foreground hover:text-foreground"
                }`} data-testid={`detail-tab-${t.key}`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {tab === "details" && <DetailsTab demo={demo} updateField={updateField} saving={saving} onDelete={onDelete} />}
          {tab === "zoom" && <ZoomTab demo={demo} onRefresh={onRefresh} />}
          {tab === "notes" && <NotesTab demo={demo} onRefresh={onRefresh} />}
          {tab === "email" && <EmailTab demo={demo} onRefresh={onRefresh} />}
          {tab === "timeline" && <TimelineTab demo={demo} />}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Details Tab ───
const DetailsTab = ({ demo, updateField, saving, onDelete }) => {
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
  return (
    <div className="space-y-5">
      {/* Status + Priority */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Status</label>
          <Select value={demo.status} onValueChange={v => updateField("status", v)} disabled={saving}>
            <SelectTrigger className="mt-1 h-9" data-testid="detail-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Priority</label>
          <Select value={demo.priority || "medium"} onValueChange={v => updateField("priority", v)} disabled={saving}>
            <SelectTrigger className="mt-1 h-9" data-testid="detail-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Assignee */}
      <div>
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Assignee</label>
        <Input defaultValue={demo.assignee || ""} placeholder="Assign to..." className="mt-1 h-9"
          onBlur={e => { if (e.target.value !== (demo.assignee || "")) updateField("assignee", e.target.value); }}
          data-testid="detail-assignee" />
      </div>

      {/* Contact Info */}
      <div className="rounded-xl border border-border p-4 space-y-2">
        <h4 className="text-xs font-semibold text-foreground flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Contact Info</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <InfoRow icon={User} label="Name" value={demo.name} />
          <InfoRow icon={Building2} label="Company" value={demo.company} />
          <InfoRow icon={Mail} label="Email" value={demo.email} copyable />
          <InfoRow icon={Phone} label="Phone" value={demo.phone} copyable />
          <InfoRow icon={Globe} label="Timezone" value={demo.timezone} />
          <InfoRow icon={Briefcase} label="Industry" value={demo.industry} />
        </div>
        {demo.interested_plan && (
          <div className="pt-2 border-t border-border">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Interested Plan</p>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-xs font-semibold" data-testid="demo-interested-plan">
              <Briefcase className="w-3 h-3" /> {demo.interested_plan}
            </div>
          </div>
        )}
        {demo.notes && (
          <div className="pt-2 border-t border-border">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Customer Notes</p>
            <p className="text-sm text-foreground">{demo.notes}</p>
          </div>
        )}
      </div>

      {/* Scheduling */}
      <div className="rounded-xl border border-border p-4 space-y-3">
        <h4 className="text-xs font-semibold text-foreground flex items-center gap-1.5"><CalendarCheck className="w-3.5 h-3.5" /> Scheduling</h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-[10px] text-muted-foreground">Requested</p>
            <p className="text-foreground font-medium">{demo.preferred_date} · {demo.preferred_time}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground">Confirmed</p>
            <p className="text-foreground font-medium">{demo.scheduled_date || "—"} · {demo.scheduled_time || "—"}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] text-muted-foreground">Scheduled Date</label>
            <Input type="date" defaultValue={demo.scheduled_date || ""} className="h-8 mt-0.5 text-sm"
              onBlur={e => { if (e.target.value !== (demo.scheduled_date || "")) updateField("scheduled_date", e.target.value); }}
              data-testid="detail-sched-date" />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground">Scheduled Time</label>
            <Input type="time" defaultValue="" className="h-8 mt-0.5 text-sm"
              onBlur={e => { if (e.target.value) updateField("scheduled_time", e.target.value); }}
              data-testid="detail-sched-time" />
          </div>
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground">Follow-Up Date</label>
          <Input type="date" defaultValue={demo.follow_up_date || ""} className="h-8 mt-0.5 text-sm"
            onBlur={e => updateField("follow_up_date", e.target.value)}
            data-testid="detail-followup" />
        </div>
      </div>

      {/* Metadata */}
      <div className="flex items-center justify-between text-[10px] text-muted-foreground/60 pt-2 border-t border-border">
        <span>ID: {demo.demo_id}</span>
        <span>Created: {fmtDate(demo.created_at)}</span>
        <Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-500 hover:text-red-600" onClick={onDelete} data-testid="detail-delete">
          <Trash2 className="w-3 h-3 mr-1" /> Delete
        </Button>
      </div>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value, copyable }) => {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <span className="text-muted-foreground text-xs w-16 shrink-0">{label}</span>
      <span className="text-foreground text-sm truncate flex-1">{value}</span>
      {copyable && (
        <button onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }}
          className="text-muted-foreground hover:text-foreground"><Copy className="w-3 h-3" /></button>
      )}
    </div>
  );
};

// ─── Zoom Tab ───
const ZoomTab = ({ demo, onRefresh }) => {
  const existing = demo.zoom_meeting || {};
  const [form, setForm] = useState({
    title: existing.title || "", date: existing.date || demo.scheduled_date || demo.preferred_date || "",
    time: existing.time || demo.scheduled_time || demo.preferred_time || "",
    timezone: existing.timezone || demo.timezone || "",
    link: existing.link || "", meeting_id: existing.meeting_id || "",
    passcode: existing.passcode || "", notes: existing.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/demo/requests/${demo.demo_id}/zoom`, form, getAuth());
      toast.success("Zoom meeting saved");
      onRefresh();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const copyAll = () => {
    const text = `${form.title || "Fieldz AI Demo"}\nDate: ${form.date} at ${form.time}${form.timezone ? ` (${form.timezone})` : ""}\nZoom: ${form.link}${form.meeting_id ? `\nID: ${form.meeting_id}` : ""}${form.passcode ? `\nPasscode: ${form.passcode}` : ""}`;
    navigator.clipboard.writeText(text);
    toast.success("Meeting info copied");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-foreground flex items-center gap-1.5"><Video className="w-4 h-4 text-blue-500" /> Zoom Meeting</h4>
        {form.link && <Button variant="outline" size="sm" className="h-7 text-xs" onClick={copyAll} data-testid="copy-zoom"><Copy className="w-3 h-3 mr-1" /> Copy All</Button>}
      </div>

      <div className="space-y-3">
        <div><label className="text-xs text-muted-foreground">Meeting Title</label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Fieldz AI Demo" className="h-9 mt-1" data-testid="zoom-title" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-muted-foreground">Date</label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="h-9 mt-1" data-testid="zoom-date" /></div>
          <div><label className="text-xs text-muted-foreground">Time</label><Input value={form.time} onChange={e => setForm({...form, time: e.target.value})} placeholder="10:00 AM" className="h-9 mt-1" data-testid="zoom-time" /></div>
        </div>
        <div><label className="text-xs text-muted-foreground">Timezone</label><Input value={form.timezone} onChange={e => setForm({...form, timezone: e.target.value})} placeholder="America/New_York" className="h-9 mt-1" /></div>
        <div><label className="text-xs text-muted-foreground">Zoom Link *</label><Input value={form.link} onChange={e => setForm({...form, link: e.target.value})} placeholder="https://zoom.us/j/..." className="h-9 mt-1" data-testid="zoom-link" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-muted-foreground">Meeting ID</label><Input value={form.meeting_id} onChange={e => setForm({...form, meeting_id: e.target.value})} className="h-9 mt-1" data-testid="zoom-id" /></div>
          <div><label className="text-xs text-muted-foreground">Passcode</label><Input value={form.passcode} onChange={e => setForm({...form, passcode: e.target.value})} className="h-9 mt-1" data-testid="zoom-passcode" /></div>
        </div>
        <div><label className="text-xs text-muted-foreground">Notes</label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} className="mt-1 resize-none" placeholder="Any instructions for the attendee..." /></div>
      </div>

      <div className="flex gap-2">
        <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white flex-1" data-testid="save-zoom">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Video className="w-4 h-4 mr-1" />}
          {demo.zoom_meeting ? "Update Meeting" : "Save Meeting"}
        </Button>
        {form.link && (
          <Button variant="outline" asChild className="shrink-0"><a href={form.link} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-4 h-4" /></a></Button>
        )}
      </div>
    </div>
  );
};

// ─── Notes Tab ───
const NotesTab = ({ demo, onRefresh }) => {
  const [text, setText] = useState("");
  const [adding, setAdding] = useState(false);
  const notes = demo.internal_notes || [];

  const addNote = async () => {
    if (!text.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/demo/requests/${demo.demo_id}/notes`, { text }, getAuth());
      setText("");
      onRefresh();
      toast.success("Note added");
    } catch { toast.error("Failed"); }
    finally { setAdding(false); }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/demo/requests/${demo.demo_id}/notes/${noteId}`, getAuth());
      onRefresh();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Textarea value={text} onChange={e => setText(e.target.value)} rows={2} placeholder="Add an internal note..." className="resize-none flex-1" data-testid="note-input" />
        <Button onClick={addNote} disabled={adding || !text.trim()} className="bg-[#FFCC00] hover:bg-[#E6B800] text-black self-end" data-testid="add-note-btn">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        </Button>
      </div>
      {notes.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">No internal notes yet</p>
      ) : (
        <div className="space-y-2">
          {[...notes].reverse().map(n => (
            <div key={n.note_id} className="rounded-lg border border-border p-3 group" data-testid={`note-${n.note_id}`}>
              <p className="text-sm text-foreground whitespace-pre-wrap">{n.text}</p>
              <div className="flex items-center justify-between mt-2 text-[10px] text-muted-foreground">
                <span>{n.author} · {new Date(n.created_at).toLocaleString()}</span>
                <button onClick={() => deleteNote(n.note_id)} className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-600 transition-opacity">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Email Tab ───
const EmailTab = ({ demo, onRefresh }) => {
  const [template, setTemplate] = useState("scheduled");
  const [customSubject, setCustomSubject] = useState("");
  const [customBody, setCustomBody] = useState("");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    setSending(true);
    try {
      const payload = { template };
      if (template === "custom") {
        payload.custom_subject = customSubject;
        payload.custom_body = customBody;
      }
      await axios.post(`${API}/demo/requests/${demo.demo_id}/send-email`, payload, getAuth());
      toast.success(`Email sent to ${demo.email}`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border p-3 text-sm">
        <p className="text-muted-foreground text-xs mb-1">Sending to</p>
        <p className="text-foreground font-medium">{demo.name} &lt;{demo.email}&gt;</p>
      </div>

      <div>
        <label className="text-xs text-muted-foreground font-medium">Template</label>
        <Select value={template} onValueChange={setTemplate}>
          <SelectTrigger className="mt-1 h-9" data-testid="email-template"><SelectValue /></SelectTrigger>
          <SelectContent>
            {EMAIL_TEMPLATES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {template === "custom" && (
        <>
          <div><label className="text-xs text-muted-foreground">Subject</label><Input value={customSubject} onChange={e => setCustomSubject(e.target.value)} className="h-9 mt-1" placeholder="Email subject..." data-testid="custom-subject" /></div>
          <div><label className="text-xs text-muted-foreground">Body</label><Textarea value={customBody} onChange={e => setCustomBody(e.target.value)} rows={5} className="mt-1" placeholder="Write your message..." data-testid="custom-body" /></div>
        </>
      )}

      {template !== "custom" && (
        <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
          {template === "scheduled" && "Will send demo scheduled confirmation with date/time."}
          {template === "zoom_details" && `Will send Zoom meeting details.${demo.zoom_meeting?.link ? "" : " (No Zoom meeting saved yet — save it first in the Zoom tab.)"}`}
          {template === "reminder" && "Will send a friendly reminder about the upcoming demo."}
          {template === "thank_you" && "Will send a thank-you message after the demo."}
          {template === "no_show_followup" && "Will send a follow-up to reschedule after a no-show."}
        </div>
      )}

      <Button onClick={handleSend} disabled={sending || (template === "custom" && (!customSubject || !customBody))}
        className="w-full bg-[#FFCC00] hover:bg-[#E6B800] text-black" data-testid="send-email-btn">
        {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
        Send Email
      </Button>
    </div>
  );
};

// ─── Timeline Tab ───
const TimelineTab = ({ demo }) => {
  const timeline = [...(demo.activity_timeline || [])].reverse();

  if (timeline.length === 0) return <p className="text-sm text-muted-foreground text-center py-8">No activity yet</p>;

  return (
    <div className="relative pl-6 space-y-0">
      <div className="absolute left-[11px] top-2 bottom-2 w-px bg-border" />
      {timeline.map((entry, i) => {
        const Icon = TIMELINE_ICONS[entry.action] || History;
        return (
          <div key={entry.entry_id || i} className="relative pb-4" data-testid={`tl-entry-${i}`}>
            <div className="absolute left-[-17px] top-0.5 w-5 h-5 rounded-full bg-background border-2 border-border flex items-center justify-center">
              <Icon className="w-2.5 h-2.5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm text-foreground">{entry.detail}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {entry.actor !== "System" && <span className="font-medium">{entry.actor} · </span>}
                {new Date(entry.timestamp).toLocaleString()}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AdminDemoRequests;
